import { Button, Flex, Text } from "@chakra-ui/react";
import { ChangeEvent, useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import {
  useGenerateRecipeChatGPTVision,
  useGenerateRecipeFromText,
} from "../../hooks";
import { ImageData } from "../../types";

type Props = {
  onSave?: () => void;
};

const ImageOCRChatgptVision = ({ onSave }: Props) => {
  const [imagesData, setImagesData] = useState<ImageData[]>([]); // Updated line

  const { register, handleSubmit } = useForm();

  const { generateRecipeFromText, isLoading: generateLoading } =
    useGenerateRecipeFromText({});

  const handleSuccess = (text: string) => {
    console.log(text);
    generateRecipeFromText(text);
    toast.loading(
      "We're currently processing this recipe. it can take between 30-60 seconds to generate it. Keep this page open and we'll let you know when it's ready!",
      { duration: 15000 }
    );
  };

  const { isLoading, uploadImages } = useGenerateRecipeChatGPTVision({
    onSuccess: handleSuccess,
  });

  const onSubmit = async () => {
    // Make a POST request with the images data
    toast.loading(
      "We're currently using AI to process these images and extract the text. it can take between 30-60 seconds to generate it. Keep this page open and we'll let you know when it's ready!",
      { duration: 15000 }
    );
    uploadImages(imagesData);
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []).slice(0, 3); // Limit to 3 files
    const validImages: ImageData[] = [];

    for (let file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is larger than 10MB.`);
        continue;
      }

      if (!file.type.startsWith("image/")) {
        toast.error(`${file.name} is not an image.`);
        continue;
      }

      const base64 = (await convertToBase64(file)) as string;
      validImages.push({ base64, type: file.type });
    }

    setImagesData(validImages);
  };

  const convertToBase64 = (file: File) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Flex p="25px" direction="column" gap="10px">
        <Text>
          Upload up to 3 images to extract the text from them. We'll then use AI
          to generate a recipe from the text. This will only generate one
          recipe. Multi-recipe support will be available shortly.
        </Text>
        <input
          type="file"
          multiple
          onChange={handleFileChange}
          accept="image/*"
        />
        <Button
          mt={4}
          colorScheme="brand"
          isLoading={isLoading || generateLoading}
          type="submit"
        >
          Upload
        </Button>
      </Flex>
    </form>
  );
};

export default ImageOCRChatgptVision;
